import React from 'react'
import {
  Button,
  Checkbox,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table
} from 'antd'
import { DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import noProductImage from 'assets/images/no-image-product.png'
import { landingPagesApi } from 'api/landingPages'
import { servicesApi } from 'api/services'
import { EServiceStatus, IAdminService } from 'models/services'
import { IMAGES_URL } from 'shared/constants/urls'
import { superAdminRoutes } from 'shared/constants/routes'
import { showServerError } from 'shared/utils/showServerError'

const STATUS_LABELS: Partial<Record<EServiceStatus, string>> = {
  [EServiceStatus.PUBLISHED]: 'Опубликована',
  [EServiceStatus.MODERATION]: 'На модерации',
  [EServiceStatus.REJECTED]: 'Отклонена',
  [EServiceStatus.ARCHIVED]: 'Архивирована'
}

// Услугу лендинга переключаем между публикацией и архивом: модерация и отказ
// остаются услугам продавцов, у услуги клуба нет владельца, которому уходит отказ
const STATUS_OPTIONS = [EServiceStatus.PUBLISHED, EServiceStatus.ARCHIVED].map(
  (value) => ({ value, label: STATUS_LABELS[value] })
)

interface PoloClubServicesProps {
  pageSlug: string
  exclusiveSlots: boolean
  onExclusiveSlotsChange: (value: boolean) => void
}

export const PoloClubServices: React.FC<PoloClubServicesProps> = ({
  pageSlug,
  exclusiveSlots,
  onExclusiveSlotsChange
}) => {
  const navigate = useNavigate()
  const [services, setServices] = React.useState<IAdminService[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [showArchived, setShowArchived] = React.useState(false)
  const [isSavingSlotsMode, setIsSavingSlotsMode] = React.useState(false)
  const [savingStatusId, setSavingStatusId] = React.useState<string | null>(null)

  const getServices = async () => {
    try {
      setIsLoading(true)
      const response = await servicesApi.getPageServices(pageSlug)
      setServices(response.data.services)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getServices()
  }, [pageSlug])

  const saveSlotsMode = async (value: boolean) => {
    try {
      setIsSavingSlotsMode(true)
      await landingPagesApi.update(pageSlug, { exclusive_slots: value })
      onExclusiveSlotsChange(value)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSavingSlotsMode(false)
    }
  }

  const confirmSlotsMode = (value: boolean) => {
    Modal.confirm({
      title: value
        ? 'Запретить одновременную запись?'
        : 'Разрешить одновременную запись?',
      content: value ? (
        <>
          Занятое время на одной услуге станет недоступно для записи на все
          остальные услуги.
          <div style={{ marginTop: 8 }}>
            Примечание: Работает при одинаковых временных слотах на всех услугах
          </div>
        </>
      ) : (
        'Слоты услуг станут независимыми: на одно и то же время можно будет записаться на разные услуги.'
      ),
      okText: value ? 'Запретить' : 'Разрешить',
      cancelText: 'Закрыть',
      onOk: () => saveSlotsMode(value)
    })
  }

  const handleDelete = async (id: string) => {
    try {
      await servicesApi.deleteService(id)
      await getServices()
    } catch (e) {
      showServerError(e)
    }
  }

  const handleStatusChange = async (id: string, status: EServiceStatus) => {
    try {
      setSavingStatusId(id)
      await servicesApi.changeServiceStatus(id, status)
      await getServices()
    } catch (e) {
      showServerError(e)
    } finally {
      setSavingStatusId(null)
    }
  }

  const displayedServices = showArchived
    ? services
    : services.filter((s) => s.status !== EServiceStatus.ARCHIVED)

  const columns: ColumnsType<IAdminService> = [
    {
      title: 'Фото',
      dataIndex: 'photo',
      width: 100,
      render: (_, { photo }) =>
        photo ? (
          <div
            style={{
              width: 80,
              height: 56,
              backgroundImage: `url(${IMAGES_URL}${photo})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: 4
            }}
          />
        ) : (
          <img src={noProductImage} alt='Услуга' width={80} height={56} />
        )
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Категория',
      key: 'category',
      width: 160,
      render: (_, { category }) =>
        typeof category === 'object' && category ? category.name : '—'
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (_, { price }) => `${price} ₽`
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (_, service) => (
        <Select
          size='small'
          style={{ width: 150 }}
          value={service.status}
          options={STATUS_OPTIONS}
          loading={savingStatusId === service._id}
          disabled={savingStatusId === service._id}
          onChange={(status) => handleStatusChange(service._id, status)}
        />
      )
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 110,
      render: (_, service) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<EditOutlined />}
            size='small'
            onClick={() =>
              navigate(
                superAdminRoutes.POLO_CLUB_SERVICE_EDIT.replace(
                  ':id',
                  service._id
                )
              )
            }
          />
          <Popconfirm
            title='Удалить услугу?'
            onConfirm={() => handleDelete(service._id)}
          >
            <Button type='primary' danger icon={<DeleteFilled />} size='small' />
          </Popconfirm>
        </div>
      )
    }
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 16
        }}
      >
        <Switch
          checked={exclusiveSlots}
          loading={isSavingSlotsMode}
          onChange={confirmSlotsMode}
        />
        <span>Запретить одновременную запись</span>
        <Checkbox
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        >
          Показать архивные
        </Checkbox>
        <Button
          type='primary'
          size='large'
          icon={<PlusOutlined />}
          onClick={() => navigate(superAdminRoutes.POLO_CLUB_SERVICE_CREATE)}
        >
          Добавить услугу
        </Button>
      </div>
      <Table
        scroll={{ x: 750 }}
        pagination={false}
        columns={columns}
        dataSource={displayedServices}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
      />
    </>
  )
}
